import { Tabs } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { useClientSideTableFilter } from "~components/DataTable/hooks/useClientSideTableFilter.ts";
import { filterByTableField } from "~components/DataTable/utils/filterByTableField.ts";
import type { UserRoles } from "~enums/userRoles";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import UserPermissionModal from "~pages/Users/components/UserPermissionModal";
import useGetUserPermissionsTableColumns from "~pages/Users/hooks/useGetUserPermissionsTableColumns.tsx";
import { useGetUserPermissionsTableFilters } from "~pages/Users/hooks/useGetUserPermissionsTableFilters.tsx";
import useGroupAndSortPermissionsByProduct from "~pages/Users/hooks/useGroupAndSortPermissionsByProduct.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import type { DataTableConfig } from "~types/dataTableConfig.ts";
import type { PermissionScopeResources } from "~types/permissionScope.ts";
import { PermissionProducts } from "../../enums/permissionProducts";
import { UserPermissionsFilterTypes } from "../../enums/userPermissionsFilterTypes.ts";
import { UserPermissionsTableColumns } from "../../enums/userPermissionsTableColumns.ts";
import type { UserPermissionTransformed } from "../../types";

import "./UserPermissionsDataTable.scss";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";

const competitionsColumnsConfig: DataTableColumnGroupDefinition[] = [
  {
    columnChooserTitleIntlKey: "userPermissions.table.column.chooser.title",
    columns: [
      { key: UserPermissionsTableColumns.UserRole, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.Leagues, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.Competitions, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.Seasons, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.SeasonStatuses, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.FixtureStatuses, defaultInColumnChooser: true },
    ],
  },
];

const teamManagerColumnsConfig: DataTableColumnGroupDefinition[] = [
  {
    columnChooserTitleIntlKey: "userPermissions.table.column.chooser.title",
    columns: [
      { key: UserPermissionsTableColumns.UserRole, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.EntityGroups, defaultInColumnChooser: true },
      { key: UserPermissionsTableColumns.Entities, defaultInColumnChooser: true },
    ],
  },
];

const userPermissionsFilterFunctions = {
  [UserPermissionsFilterTypes.UserRoles]: filterByTableField<UserPermissionTransformed>(UserPermissionsTableColumns.UserRole),
};

type Props = {
  permissions: UserPermissionTransformed[] | undefined;
  permissionScopeResources: PermissionScopeResources;
  isLoading: boolean;
  readOnly: boolean;
  onEditPermissionClick?: (userPermission: UserPermissionTransformed) => void;
  onDeletePermissionClick?: (userPermission: UserPermissionTransformed) => void;
  onRefresh?: () => Promise<void>;
  onProductTabChange?: (product: PermissionProducts) => void;
  initialProduct?: PermissionProducts;
  initialUserRoles?: UserRoles[];
  localStorageUniqueId: string;
};

const UserPermissionsDataTable = ({
  permissions,
  permissionScopeResources,
  isLoading,
  readOnly,
  onEditPermissionClick,
  onDeletePermissionClick,
  onRefresh,
  onProductTabChange,
  initialProduct,
  initialUserRoles,
  localStorageUniqueId,
}: Props) => {
  const { formatMessage } = useIntl();
  const [currentUserPermissionForView, setCurrentUserPermissionForView] = useState<UserPermissionTransformed | null>(null);
  const [selectedProductTab, setSelectedProductTab] = useState<PermissionProducts | undefined>(initialProduct);
  const { hasPermission } = useUserPermissions();

  const permissionsByProduct = useGroupAndSortPermissionsByProduct(permissions);
  const availableProducts = Object.keys(permissionsByProduct) as PermissionProducts[];
  const currentProductTab = selectedProductTab || availableProducts[0];

  const { filters, currentFilter, handleFilterChange, resetFilters } = useGetUserPermissionsTableFilters({
    currentProductTab,
    initialUserRoles,
  });

  const productTabs = [
    ...(availableProducts.includes(PermissionProducts.Competitions)
      ? [
          {
            key: PermissionProducts.Competitions,
            label: formatMessage({ id: "permissions.product.label.competitions" }),
          },
        ]
      : []),
    ...(availableProducts.includes(PermissionProducts.TeamManager)
      ? [{ key: PermissionProducts.TeamManager, label: formatMessage({ id: "permissions.product.label.teamManager" }) }]
      : []),
  ];

  const productPermissions = permissionsByProduct[currentProductTab] || [];

  const { filteredData: filteredPermissions } = useClientSideTableFilter({
    data: productPermissions,
    currentFilter,
    filterFunctions: userPermissionsFilterFunctions,
  });

  const onViewPermissionClick = (userPermission: UserPermissionTransformed) => setCurrentUserPermissionForView(userPermission);

  const onHandleProductTabChange = useCallback(
    (product: PermissionProducts) => {
      setSelectedProductTab(product);
      onProductTabChange?.(product);
      resetFilters();
    },
    [resetFilters, onProductTabChange],
  );

  useEffect(() => {
    if (currentProductTab) {
      onProductTabChange?.(currentProductTab);
    }
  }, [currentProductTab, onProductTabChange]);

  const competitionsColumns = useGetUserPermissionsTableColumns(PermissionProducts.Competitions);

  const teamManagerColumns = useGetUserPermissionsTableColumns(PermissionProducts.TeamManager);

  const columnsByProduct: Record<PermissionProducts, typeof competitionsColumns> = {
    [PermissionProducts.Competitions]: competitionsColumns,
    [PermissionProducts.TeamManager]: teamManagerColumns,
  };

  useEffect(() => {
    if (currentProductTab && !availableProducts.includes(currentProductTab)) {
      onHandleProductTabChange(availableProducts[0]);
    }
  }, [currentProductTab, availableProducts, onHandleProductTabChange]);

  const dataTableUiConfiguration: DataTableConfig = useMemo(
    () => ({
      columnsConfig: currentProductTab === PermissionProducts.TeamManager ? teamManagerColumnsConfig : competitionsColumnsConfig,
      filtersConfig: [{ filterKey: UserPermissionsFilterTypes.UserRoles }],
    }),
    [currentProductTab],
  );

  const columnChooserLocalStorageKey = `tables.settings.columns.${localStorageUniqueId}.${currentProductTab}`;
  const sorterLocalStorageKey = `tables.settings.sort.${localStorageUniqueId}.${currentProductTab}`;

  const paginationConfig = useHandlePagination();

  const hasPermissionToUpdate = hasPermission(PermissionNames.PERMISSIONS, PermissionOperations.UPDATE);
  const hasPermissionToDelete = hasPermission(PermissionNames.PERMISSIONS, PermissionOperations.DELETE);
  return (
    <>
      {productTabs.length > 0 && (
        <Tabs
          rootClassName="permission-user-role-tabs"
          size="large"
          activeKey={currentProductTab}
          onChange={(value) => onHandleProductTabChange(value as PermissionProducts)}
          items={productTabs}
        />
      )}
      <DataTable<UserPermissionTransformed>
        key={currentProductTab}
        columns={columnsByProduct[currentProductTab] || competitionsColumns}
        rowNameKey={"userRole"}
        rowActions={{
          base: {
            view: {
              onClick: onViewPermissionClick,
              hidden: () => false,
            },
            manage: {
              onClick: onEditPermissionClick,
              hidden: () => readOnly || !hasPermissionToUpdate,
              disabled: (permission) => permission.isGroupPermission || permission.disabled,
            },
            delete: {
              onClick: onDeletePermissionClick,
              hidden: () => readOnly || !hasPermissionToDelete,
              disabled: (permission) => permission.isGroupPermission || permission.disabled,
              itemName: (permission) => formatMessage({ id: `permissions.userRole.${permission.userRole}` }),
            },
          },
        }}
        dataSource={filteredPermissions}
        isDataLoading={isLoading}
        rowKey="permissionId"
        permissionNames={[PermissionNames.PERMISSIONS]}
        addButtonIntlKey="permissions.user.role.actions.add"
        columnChooserLocalStorageKey={columnChooserLocalStorageKey}
        dataTableUiConfiguration={dataTableUiConfiguration}
        columnSortConfig={{
          sorterLocalStorageKey: sorterLocalStorageKey,
          defaultSorter: { columnKey: UserPermissionsTableColumns.UserRole, order: "ascend" },
        }}
        filtersConfig={{
          filters,
          currentFilter,
          handleFilterChange,
        }}
        handleRefresh={onRefresh}
        paginationConfig={{ ...paginationConfig, total: filteredPermissions.length }}
      />
      <UserPermissionModal
        userPermission={currentUserPermissionForView}
        permissionScopeResources={permissionScopeResources}
        onClose={() => setCurrentUserPermissionForView(null)}
        isOpen={!!currentUserPermissionForView}
      />
    </>
  );
};

export default UserPermissionsDataTable;
